import React, { useContext, useState } from "react"
import moment from "moment"

import { funEU } from "../../utils/firebase"
import { AlertsContext } from "../../utils/providers/alerts"
import { AuthContext } from "../../utils/providers/auth"
import { formatPointsValue } from "../../utils/stableford"
import "./points-partner.scss"

// UI components
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import WindowCard from "../../components/ui/window-card/window-card"
import Input from "../../components/ui/inputs/input"
import Textarea from "../../components/ui/inputs/textarea"
import Button from "../../components/ui/button/button"
import Table from "../../components/structure/table/table"
import Badge from "../../components/ui/badge/badge"

const lookupClient = funEU.httpsCallable("partnerLookupClientPoints")
const redeemPoints = funEU.httpsCallable("partnerRedeemPoints")

const formatDate = (millis) => {
	if (!millis) return "-"
	return moment(millis).format("DD/MM/YYYY HH:mm")
}

/**
 * Redemption partner page.
 *
 * The only page a `is_points_partner` account can reach. They look a client up
 * by email, see the Stableford balance and what it is worth, then redeem some or
 * all of it against one of their own booking references.
 *
 * Nothing here touches Firestore directly - every read and write goes through
 * the callables in the `points` codebase, which resolve the partner's identity
 * server-side and return only the fields below.
 */
export default function PointsPartner() {
	const [email, setEmail] = useState("")
	const [searching, setSearching] = useState(false)
	const [searchedEmail, setSearchedEmail] = useState("")
	const [clients, setClients] = useState(null)
	const [selectedClientID, setSelectedClientID] = useState("")

	const [points, setPoints] = useState("")
	const [reference, setReference] = useState("")
	const [note, setNote] = useState("")
	const [redeeming, setRedeeming] = useState(false)

	const { pushAlert } = useContext(AlertsContext)
	const { user } = useContext(AuthContext)

	const partnerName = user?.points_partner?.name || "Partner"
	const selectedClient = (clients || []).find((client) => client.id === selectedClientID) || null

	// Points typed into the form, as a number we can validate against the balance
	const pointsToRedeem = Number(points)
	const pointsAreValid = Number.isInteger(pointsToRedeem) && pointsToRedeem > 0
	const pointsExceedBalance = pointsAreValid && selectedClient && pointsToRedeem > selectedClient.balance
	const canRedeem = Boolean(selectedClient) && pointsAreValid && !pointsExceedBalance && reference.trim().length > 0

	const resetRedemptionForm = () => {
		setPoints("")
		setReference("")
		setNote("")
	}

	/**
	 * Look the email address up. `keepClientID` is passed when refreshing after
	 * a redemption, so a client picked out of several matches stays selected.
	 */
	const runSearch = async (keepClientID = "") => {
		setSearching(true)
		setClients(null)
		setSelectedClientID("")

		if (!keepClientID) resetRedemptionForm()

		try {
			const { data } = await lookupClient({ email })
			const matches = data?.clients || []

			setClients(matches)
			setSearchedEmail(data?.email || email)

			// A single match is the normal case, so skip straight to it
			if (keepClientID && matches.some((match) => match.id === keepClientID)) {
				setSelectedClientID(keepClientID)
			} else if (matches.length === 1) {
				setSelectedClientID(matches[0].id)
			}

			if (matches.length === 0) {
				pushAlert({
					type: "ALERT",
					title: "No client found",
					body: "No Golf Holidays Direct client is registered with that email address.",
				})
			}
		} catch (error) {
			pushAlert({
				type: "ERROR",
				title: "Search failed",
				body: error?.message || "Unable to look that client up.",
			})
		} finally {
			setSearching(false)
		}
	}

	const submitRedemption = async () => {
		setRedeeming(true)
		const clientID = selectedClient.id

		try {
			const { data } = await redeemPoints({
				clientID,
				points: pointsToRedeem,
				reference: reference.trim(),
				note: note.trim(),
			})

			pushAlert({
				type: "SUCCESS",
				title: "Points redeemed",
				body: `${data.points} points (${formatPointsValue(data.points)}) taken off ${data.clientName || "the client"} against ${data.reference}.`,
			})

			// Re-read the balance rather than adjusting it locally, so the figure
			// on screen is always the one the server just committed.
			resetRedemptionForm()
			await runSearch(clientID)
		} catch (error) {
			pushAlert({
				type: "ERROR",
				title: "Redemption failed",
				body: error?.message || "Unable to redeem those points.",
			})
		} finally {
			setRedeeming(false)
		}
	}

	return (
		<Tile>
			<Title>
				<h1>Stableford points</h1>
				<p>
					Check a Golf Holidays Direct client's Stableford balance and redeem it against a {partnerName} booking. Every 10 points is worth £1.00.
				</p>
			</Title>

			<div className="points-partner">
				<WindowCard
					title="Find a client"
					subtitle="Search for the client using their email address.">
					<div className="points-partner__search">
						<Input
							type="email"
							label="Client email address"
							placeholder="Client email address"
							showLabel={true}
							value={email}
							onChange={setEmail}
							onKeyPress={(event) => {
								if (event.key === "Enter" && email.trim()) runSearch()
							}}
						/>

						<Button
							label="Find client"
							loading={searching}
							loadingText="Searching..."
							disabled={!email.trim()}
							onClick={() => runSearch()}
						/>
					</div>
				</WindowCard>

				{/* More than one record can share an email address, so let them pick */}
				{clients && clients.length > 1 && (
					<WindowCard
						className="has-top-margin"
						title="Multiple clients found"
						subtitle={`${clients.length} client records match ${searchedEmail}. Choose the one you are booking for.`}>
						<div className="points-partner__choices">
							{clients.map((client) => (
								<button
									key={client.id}
									type="button"
									className={["points-partner__choice", client.id === selectedClientID && "is-selected"].filter(Boolean).join(" ")}
									onClick={() => {
										setSelectedClientID(client.id)
										resetRedemptionForm()
									}}>
									<span className="points-partner__choice-name">{client.name || "Unnamed client"}</span>
									{/* The stored address can differ from the one searched (a corrected typo or mixed case), so show it */}
									<span className="points-partner__choice-email">{client.email}</span>
									<span className="points-partner__choice-meta">
										{client.balance} points / {formatPointsValue(client.balance)}
									</span>
								</button>
							))}
						</div>
					</WindowCard>
				)}

				{selectedClient && (
					<>
						<WindowCard
							className="has-top-margin"
							title={selectedClient.name || "Client"}
							subtitle={selectedClient.email}>
							<div className="points-partner__summary">
								<div className="points-partner__stat is-primary">
									<p className="points-partner__stat-label">Available balance</p>
									<p className="points-partner__stat-value">{selectedClient.balance.toLocaleString("en-GB")}</p>
									<p className="points-partner__stat-meta">points</p>
								</div>

								<div className="points-partner__stat is-primary">
									<p className="points-partner__stat-label">Worth</p>
									<p className="points-partner__stat-value">{formatPointsValue(selectedClient.balance)}</p>
									<p className="points-partner__stat-meta">10 points = £1.00</p>
								</div>

								<div className="points-partner__stat">
									<p className="points-partner__stat-label">Earned to date</p>
									<p className="points-partner__stat-value">{selectedClient.earned.toLocaleString("en-GB")}</p>
									<p className="points-partner__stat-meta">across completed holidays</p>
								</div>

								<div className="points-partner__stat">
									<p className="points-partner__stat-label">Already spent</p>
									<p className="points-partner__stat-value">{selectedClient.spent.toLocaleString("en-GB")}</p>
									<p className="points-partner__stat-meta">on previous redemptions</p>
								</div>
							</div>
						</WindowCard>

						<WindowCard
							className="has-top-margin"
							title="Redeem points"
							subtitle="Deduct points from this client's balance and record which of your bookings they were used on.">
							{selectedClient.balance > 0 ? (
								<>
									<div className="points-partner__form">
										<Input
											type="number"
											label="Points to redeem"
											placeholder="Points to redeem"
											showLabel={true}
											value={points}
											onChange={setPoints}
											error={pointsExceedBalance ? `Only ${selectedClient.balance} points are available.` : ""}
											note={pointsAreValid && !pointsExceedBalance ? `Worth ${formatPointsValue(pointsToRedeem)}` : `Up to ${selectedClient.balance} points`}
										/>

										<Input
											label="GB booking ref"
											placeholder="GB booking ref"
											showLabel={true}
											value={reference}
											onChange={setReference}
											note="Golf Breaks booking reference"
										/>

										<Textarea
											wrapperClass="points-partner__note"
											label="Note (optional)"
											placeholder="Note (optional)"
											showLabel={true}
											rows={3}
											value={note}
											onChange={setNote}
										/>
									</div>

									<div className="points-partner__actions">
										<Button
											label={pointsAreValid && !pointsExceedBalance ? `Redeem ${pointsToRedeem} points (${formatPointsValue(pointsToRedeem)})` : "Redeem points"}
											loading={redeeming}
											loadingText="Redeeming..."
											disabled={!canRedeem}
											onClick={() => submitRedemption()}
										/>

										<Button
											label="Use full balance"
											type="secondary"
											disabled={redeeming}
											onClick={() => setPoints(String(selectedClient.balance))}
										/>
									</div>

									<p className="points-partner__hint">
										Redemptions are final. If you need one reversed, contact lewis.newson@golfholidaysdirect.com.
									</p>
								</>
							) : (
								<p className="points-partner__empty">This client has no Stableford points available to redeem.</p>
							)}
						</WindowCard>

						{selectedClient.redemptions.length > 0 && (
							<WindowCard
								className="has-top-margin"
								title="Your previous redemptions for this client"
								subtitle="Points you have already taken off this client's balance.">
								<Table
									headings={["Date", "Reference", "Points", "Value", "Status"]}
									noResults={false}>
									{selectedClient.redemptions.map((redemption) => (
										<tr key={redemption.id}>
											<td>{formatDate(redemption.created)}</td>
											<td>{redemption.reference}</td>
											<td>{redemption.points}</td>
											<td>{formatPointsValue(redemption.points)}</td>
											<td>
												<Badge
													type={redemption.voided ? "NEGATIVE" : "POSITIVE"}
													label={redemption.voided ? "Voided" : "Applied"}
												/>
											</td>
										</tr>
									))}
								</Table>
							</WindowCard>
						)}
					</>
				)}

			</div>
		</Tile>
	)
}
